import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { QRCodeCanvas } from 'qrcode.react';
import { QrCode2, Business } from '@mui/icons-material';

const BrandedQRCode = ({ 
  value, 
  size = 100, 
  title = "Hindalco Industries", 
  subtitle = "QR Management System",
  showBranding = true,
  variant = "standard", // "standard", "compact", "minimal"
  logoUrl = null, // Custom logo URL
  logoSize = 0.25 // Logo size as fraction of QR size
}) => {
  const theme = useTheme();
  
  // Brand colors based on the new theme
  const brandColors = {
    primary: '#1F425C', // Dark blue from login page
    secondary: '#AC1E15', // Red from login page buttons
    accent: '#F0CF55', // Gold/yellow accent from login page
    text: '#1F425C', // Dark blue text
    white: '#ffffff',
    red: '#AC1E15', // Red accent
    yellow: '#F0CF55' // Gold accent
  };

  if (variant === "minimal") {
    return (
      <Box sx={{ 
        display: 'inline-block',
        p: 2,
        bgcolor: brandColors.white,
        borderRadius: 2,
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        border: `2px solid ${brandColors.accent}`,
      }}>
        <QRCodeCanvas
          value={value || ''}
          size={size}
          level="H"
          includeMargin={false}
          fgColor={brandColors.primary}
          bgColor={brandColors.white}
        />
      </Box>
    );
  }

  if (variant === "compact") {
    return (
      <Box sx={{ 
        display: 'inline-block',
        bgcolor: brandColors.white,
        borderRadius: 3,
        boxShadow: '0 12px 32px rgba(26, 54, 93, 0.18)',
        border: `2px solid ${brandColors.primary}`,
        overflow: 'hidden',
        position: 'relative'
      }}>
        {/* Compact Header */}
        <Box sx={{
          background: `linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.secondary} 100%)`,
          py: 1,
          px: 2,
          textAlign: 'center'
        }}>
          <Typography variant="subtitle2" sx={{ 
            color: brandColors.white,
            fontWeight: 700,
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.3px'
          }}>
            {title}
          </Typography>
        </Box>
        
        {/* QR Code Section */}
        <Box sx={{ p: 1.5, textAlign: 'center', position: 'relative' }}>
          <Box sx={{ 
            position: 'relative',
            display: 'inline-block'
          }}>
            <QRCodeCanvas
              value={value || ''}
              size={size}
              level="H"
              includeMargin={false}
              fgColor={brandColors.primary}
              bgColor={brandColors.white}
            />
            
            {/* Central logo for compact version */}
            <Box sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              bgcolor: brandColors.white,
              borderRadius: 0.4,
              p: 0.2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: size * logoSize,
              height: size * logoSize,
              boxShadow: `0 3px 12px rgba(26, 54, 93, 0.4)`,
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: -0.5,
                borderRadius: 0.4,
                zIndex: -1
              }
            }}>
              {logoUrl ? (
                <Box
                  component="img"
                  src={logoUrl}
                  alt="Company Logo"
                  sx={{
                    width: '76%',
                    height: '76%',
                    objectFit: 'contain'
                  }}
                />
              ) : (
                <Business sx={{ 
                  fontSize: size * (logoSize * 0.5),
                  color: brandColors.primary,
                  fontWeight: 'bold'
                }} />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  // Standard variant - full branding like the reference image
  return (
    <Box sx={{ 
      display: 'inline-block',
      bgcolor: brandColors.white,
      borderRadius: 4,
      boxShadow: '0 20px 50px rgba(26, 54, 93, 0.25)',
      overflow: 'hidden',
      position: 'relative',
      maxWidth: size + 40,
      border: `3px solid ${brandColors.primary}`,
    }}>
      {/* Header Section - like "Our Website" in the reference */}
      {showBranding && (
        <Box sx={{ 
          background: `linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.secondary} 100%)`,
          py: 2,
          px: 3,
          textAlign: 'center',
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '3px',
            background: `linear-gradient(90deg, transparent 0%, ${brandColors.white} 50%, transparent 100%)`,
            opacity: 0.3
          }
        }}>
          <Typography variant="h6" sx={{ 
            color: brandColors.white,
            fontWeight: 700,
            fontSize: '1rem',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Hindalco Industries
          </Typography>
          <Typography variant="caption" sx={{ 
            color: brandColors.white,
            fontWeight: 500,
            fontSize: '0.7rem',
            opacity: 0.9,
            display: 'block'
          }}>
            {subtitle}
          </Typography>
        </Box>
      )}
      
      {/* QR Code Section with Central Logo */}
      <Box sx={{ 
        p: 2,
        textAlign: 'center',
        bgcolor: brandColors.white,
        position: 'relative'
      }}>
        {/* QR Code with corner styling */}
        <Box sx={{ 
          position: 'relative',
          display: 'inline-block',
          p: 1,
          '&::before, &::after': {
            content: '""',
            position: 'absolute',
            width: '20px',
            height: '20px',
            border: `3px solid ${brandColors.primary}`,
          },
          '&::before': {
            top: 0,
            left: 0,
            borderRight: 'none',
            borderBottom: 'none',
          },
          '&::after': {
            bottom: 0,
            right: 0,
            borderLeft: 'none',
            borderTop: 'none',
          }
        }}>
          <QRCodeCanvas
            value={value || ''}
            size={size}
            level="H"
            includeMargin={false}
            fgColor={brandColors.primary}
            bgColor={brandColors.white}
          />
          
          {/* Central Logo/Icon - positioned over QR code center */}
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: brandColors.white,
            border: `2px solid ${brandColors.primary}`,
            borderRadius: 1,
            p: 0.3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: size * logoSize,
            height: size * logoSize,
            boxShadow: `0 4px 16px rgba(26, 54, 93, 0.4)`,
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: -1,
              borderRadius: 1,
              zIndex: -1
            }
          }}>
            {logoUrl ? (
              <Box
                component="img"
                src={logoUrl}
                alt="Company Logo"
                sx={{
                  width: '75%',
                  height: '75%',
                  objectFit: 'contain'
                }}
              />
            ) : (
              <Business sx={{ 
                fontSize: size * (logoSize * 0.6),
                color: brandColors.primary,
                fontWeight: 'bold'
              }} />
            )}
          </Box>
        </Box>
        
        {/* Decorative corner markers */}
        <Box sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          width: 12,
          height: 12,
          bgcolor: brandColors.primary,
          borderRadius: '50%'
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: 8,
          left: 8,
          width: 12,
          height: 12,
          bgcolor: brandColors.primary,
          borderRadius: '50%'
        }} />
      </Box>
      
      {/* Footer Section */}
      {showBranding && (
        <Box sx={{ 
          background: `linear-gradient(135deg, #f1f5f9 0%, ${brandColors.white} 100%)`,
          py: 1.5,
          px: 3,
          borderTop: `1px solid ${brandColors.accent}`,
          textAlign: 'center'
        }}>
          <Typography variant="caption" sx={{ 
            color: brandColors.text,
            fontWeight: 600,
            fontSize: '0.65rem',
            opacity: 0.8
          }}>
            Scan to access package details
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default BrandedQRCode; 